<template>
  <div class="video-list">
    <!-- 移动端顶部成绩类型筛选 -->
    <div class="mobile-score-type-filter">
      <div
        v-for="type in scoreTypes"
        :key="type.id"
        :class="['filter-item', { active: selectedScoreType === type.id }]"
        @click="selectScoreType(type.id)"
      >
        {{ type.name }}
      </div>
    </div>
    
    <div class="list-container">
      <!-- 左侧分类栏 -->
      <aside class="category-sidebar">
        <!-- PC端成绩类型筛选 -->
        <div class="score-type-filter">
          <div
            v-for="type in scoreTypes"
            :key="type.id"
            :class="['filter-item', { active: selectedScoreType === type.id }]"
            @click="selectScoreType(type.id)"
          >
            {{ type.name }}
          </div>
        </div>
        
        <ul class="category-list">
          <li
            v-for="category in categories"
            :key="category.id"
            :class="['category-item', { active: selectedCategory === category.id }]"
            @click="selectCategory(category.id)"
          >
            <span class="category-name">{{ category.name }}</span>
          </li>
        </ul>
      </aside>

      <!-- 右侧视频列表 -->
      <main class="video-content" ref="videoContent" @scroll="handleScroll">
        <div class="video-grid" v-if="filteredVideos.length > 0">
          <div
            v-for="video in filteredVideos"
            :key="video.id"
            class="video-card"
            @click="goToDetail(video.id)"
          >
            <div class="video-thumbnail">
              <img :src="video.thumbnail" :alt="video.title" />
            </div>
            <div class="video-info">
              <div class="video-meta">
                <span class="video-student">{{ video.studentName }}</span>
                <span class="video-score">{{ formatScore(video.score) }}分</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading && filteredVideos.length === 0" class="loading">
          <p>加载中...</p>
        </div>
        <div v-else-if="!loading && filteredVideos.length === 0" class="no-results">
          <p>没有找到相关视频</p>
        </div>
        
        <div v-if="loadingMore" class="loading-more">
          <p>加载更多...</p>
        </div>
        <div v-if="hasMore === false && filteredVideos.length > 0" class="no-more">
          <p>已加载全部数据</p>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { categories } from '../data/videos'
import { queryScores } from '../api/scoreApi'

export default {
  name: 'VideoList',
  data() {
    return {
      videos: [],
      categories: categories,
      selectedCategory: 'all',
      selectedScoreType: '音教成绩',
      scoreTypes: [
        { id: '音教成绩', name: '音教成绩' },
        { id: '器表成绩', name: '器表成绩' },
        { id: '声表成绩', name: '声表成绩' }
      ],
      loading: false,
      loadingMore: false,
      hasMore: true,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      }
    }
  },
  computed: {
    filteredVideos() {
      return this.videos
    }
  },
  watch: {
    selectedScoreType() {
      this.resetAndLoad()
    },
    selectedCategory() {
      this.resetAndLoad()
    }
  },
  mounted() {
    this.loadVideos()
  },
  beforeDestroy() {
    // 清理滚动监听
    if (this.$refs.videoContent) {
      this.$refs.videoContent.removeEventListener('scroll', this.handleScroll)
    }
  },
  methods: {
    resetAndLoad() {
      // 重置数据并重新加载
      this.videos = []
      this.pagination.page = 1
      this.hasMore = true
      this.loadVideos()
    },
    async loadVideos(append = false) {
      // 如果是追加数据，使用 loadingMore；否则使用 loading
      if (append) {
        this.loadingMore = true
      } else {
        this.loading = true
      }
      
      // 获取分数段范围
      // 当选择"全部"时，使用230-290的范围（覆盖所有分数段）
      let minScore = 230
      let maxScore = 290
      
      if (this.selectedCategory !== 'all') {
        const selectedCat = this.categories.find(cat => cat.id === this.selectedCategory)
        if (selectedCat) {
          minScore = selectedCat.min
          maxScore = selectedCat.max
        }
      }

      try {
        const result = await queryScores({
          scoreType: this.selectedScoreType,
          minScore: minScore,
          maxScore: maxScore,
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        })

        if (result.success) {
          const list = result.data.list || []
          
          if (append) {
            // 追加数据
            this.videos = [...this.videos, ...list]
          } else {
            // 替换数据
            this.videos = list
          }
          
          this.pagination = {
            ...this.pagination,
            total: result.data.pagination?.total || 0,
            totalPages: result.data.pagination?.total_pages || 0
          }
          
          // 判断是否还有更多数据
          this.hasMore = this.pagination.page < this.pagination.totalPages
        } else {
          console.error('加载失败:', result.message, result.errors)
          if (!append) {
            this.videos = []
          }
        }
      } catch (error) {
        console.error('请求错误:', error)
        if (!append) {
          this.videos = []
        }
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    handleScroll(event) {
      const target = event.target
      const scrollTop = target.scrollTop
      const scrollHeight = target.scrollHeight
      const clientHeight = target.clientHeight
      
      // 距离底部 100px 时开始加载
      if (scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore()
      }
    },
    loadMore() {
      // 如果正在加载或没有更多数据，则不加载
      if (this.loadingMore || !this.hasMore) {
        return
      }
      
      // 加载下一页
      this.pagination.page += 1
      this.loadVideos(true)
    },
    selectScoreType(typeId) {
      this.selectedScoreType = typeId
      this.pagination.page = 1 // 重置页码
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId
      this.pagination.page = 1 // 重置页码
    },
    goToDetail(id) {
      this.$router.push(`/video/${id}`)
    },
    formatScore(score) {
      return parseFloat(score).toFixed(2)
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.video-list {
  padding: 0;
  height: 100%;
  overflow: hidden;
}

.list-container {
  display: flex;
  height: 100%;
  position: relative;
}

/* 左侧分类栏 */
.category-sidebar {
  width: 150px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 0;
  overflow-y: auto;
  flex-shrink: 0;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  height: 100vh;
  z-index: 10;
}

/* 成绩类型筛选 */
.score-type-filter {
  padding: 0 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1rem;
}

.filter-item {
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.875rem;
  text-align: center;
  transition: all 0.3s;
  color: #666;
  background: #f5f5f5;
}

.filter-item:hover {
  background-color: #fff4ed;
  color: #ed6d00;
}

.filter-item.active {
  background-color: #fff4ed;
  color: #ed6d00;
  font-weight: 500;
}

.category-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.category-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  border-left: 3px solid transparent;
}

.category-item:hover {
  background-color: #f5f5f5;
}

.category-item.active {
  background-color: #fff4ed;
  border-left-color: #ed6d00;
  color: #ed6d00;
}

.category-name {
  font-size: 0.9375rem;
  font-weight: 500;
}

/* 右侧视频内容 */
.video-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  background: #f5f5f5;
  margin-left: 150px;
  height: 100vh;
}


.video-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.video-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 比例 */
  background: #000;
  overflow: hidden;
}

.video-thumbnail img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  display: block;
}

.video-info {
  padding: 0.5rem 1rem;
}

.video-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-description {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.5rem;
}

.video-score {
  color: #ed6d00;
  font-weight: 500;
}

.video-student {
  color: #000;
  font-weight: 500;
}

.loading {
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
  font-size: 1.125rem;
}

.loading-more {
  text-align: center;
  padding: 2rem;
  color: #999;
  font-size: 0.9375rem;
}

.no-more {
  text-align: center;
  padding: 2rem;
  color: #999;
  font-size: 0.9375rem;
}

.no-results {
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
  font-size: 1.125rem;
}

/* 移动端顶部成绩类型筛选 */
.mobile-score-type-filter {
  display: none;
}

/* 移动端样式 */
@media (max-width: 768px) {
  .video-list {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* 移动端顶部成绩类型筛选 */
  .mobile-score-type-filter {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
    padding: 0.75rem 1rem;
    z-index: 20;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .mobile-score-type-filter .filter-item {
    flex: 1;
    padding: 0.5rem 0.75rem;
    margin: 0 0.25rem;
    cursor: pointer;
    border-radius: 6px;
    font-size: 0.875rem;
    text-align: center;
    transition: all 0.3s;
    color: #666;
    background: #f5f5f5;
  }

  .mobile-score-type-filter .filter-item:first-child {
    margin-left: 0;
  }

  .mobile-score-type-filter .filter-item:last-child {
    margin-right: 0;
  }

  .mobile-score-type-filter .filter-item:hover {
    background-color: #fff4ed;
    color: #ed6d00;
  }

  .mobile-score-type-filter .filter-item.active {
    background-color: #fff4ed;
    color: #ed6d00;
    font-weight: 500;
  }

  .list-container {
    flex-direction: row;
    margin-top: 0;
    height: 100vh;
  }

  .category-sidebar {
    width: 90px;
    padding: 0;
    border-right: 1px solid #e0e0e0;
    position: fixed;
    left: 0;
    top: 60px; /* 从顶部筛选栏下方开始 */
    bottom: 0;
    height: calc(100vh - 60px);
  }

  .video-content {
    margin-left: 90px;
    margin-top: 60px; /* 从顶部筛选栏下方开始 */
    padding-top: 0;
    height: calc(100vh - 60px);
  }

  /* PC端成绩类型筛选在移动端隐藏 */
  .score-type-filter {
    display: none;
  }

  .category-item {
    padding: 0.625rem 0.5rem;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-left: none;
    border-bottom: 2px solid transparent;
  }

  .category-item.active {
    border-left: none;
    border-bottom-color: #ed6d00;
    background-color: #fff4ed;
  }

  .category-name {
    font-size: 0.75rem;
  }

  .video-content {
    padding: 0 1rem 1rem 1rem;
  }

  .video-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

/* 超小屏幕 */
@media (max-width: 480px) {
  .mobile-score-type-filter {
    padding: 0.5rem 0.75rem;
  }

  .mobile-score-type-filter .filter-item {
    font-size: 0.75rem;
    padding: 0.375rem 0.5rem;
    margin: 0 0.125rem;
  }

  .category-sidebar {
    width: 80px;
    position: fixed;
    left: 0;
    top: 60px;
    bottom: 0;
    height: calc(100vh - 60px);
  }

  .video-content {
    margin-left: 80px;
  }

  .category-name {
    font-size: 0.6875rem;
  }
}
</style>

